<template>
  <v-sheet class="public-month-grid" rounded="xl">
    <div class="public-month-grid__header">
      <div class="public-month-grid__title">{{ monthTitle }}</div>
      <div class="d-flex align-center">
        <v-btn text small class="text-capitalize mr-2 public-month-grid__today" @click="goToday">Сегодня</v-btn>
        <v-btn icon outlined small class="mr-1 public-month-grid__nav-btn" @click="shiftMonth(-1)">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon outlined small class="public-month-grid__nav-btn" @click="shiftMonth(1)">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="public-month-grid__weekdays">
      <span v-for="label in weekdayLabels" :key="label">{{ label }}</span>
    </div>

    <div class="public-month-grid__days">
      <button
        v-for="day in days"
        :key="day.date"
        type="button"
        class="public-month-grid__day"
        :class="{
          'public-month-grid__day--muted': !day.inCurrentMonth,
          'public-month-grid__day--has-slots': day.hasSlots && !day.isPast,
          'public-month-grid__day--selected': day.date === selectedDate,
          'public-month-grid__day--past': day.isPast,
        }"
        @click="onDayClick(day)"
      >
        {{ day.dayNumber }}
      </button>
    </div>
  </v-sheet>
</template>

<script>
// Отдельная (не переиспользующая Admin/MonthGrid.vue) гостевая сетка месяца — макет
// задаёт для неё другой визуальный язык (крупнее radius/отступы, обведённые кнопки
// навигации, без бэйджей "своб./занято", которые нужны только админу). Данные и
// событийный контракт (props/emits) те же, чтобы PublicBooking.vue менялся минимально.
export default {
  name: 'PublicMonthGrid',
  props: {
    month: { type: String, required: true }, // 'YYYY-MM'
    selectedDate: { type: String, default: '' },
    // Даты, на которые точно есть что забронировать (см. store/publicCalendar.js, loadMonthAvailability).
    slotDates: { type: Array, default: () => [] },
  },
  data() {
    return {
      weekdayLabels: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    };
  },
  computed: {
    monthTitle() {
      return this.$moment(this.month, 'YYYY-MM').locale('ru').format('MMMM YYYY');
    },
    days() {
      const monthStart = this.$moment(this.month, 'YYYY-MM').locale('ru').startOf('month');
      const gridStart = monthStart.clone().startOf('isoWeek');
      const monthEnd = monthStart.clone().endOf('month');
      const gridEnd = monthEnd.clone().endOf('isoWeek');

      const slotDatesSet = new Set(this.slotDates);
      const today = this.$moment().format('YYYY-MM-DD');

      const result = [];
      const cursor = gridStart.clone();
      while (cursor.isSameOrBefore(gridEnd, 'day')) {
        const dateStr = cursor.format('YYYY-MM-DD');
        result.push({
          date: dateStr,
          dayNumber: cursor.date(),
          inCurrentMonth: cursor.isSame(monthStart, 'month'),
          hasSlots: slotDatesSet.has(dateStr),
          isPast: dateStr < today,
        });
        cursor.add(1, 'day');
      }
      return result;
    },
  },
  methods: {
    shiftMonth(delta) {
      const next = this.$moment(this.month, 'YYYY-MM').add(delta, 'month').format('YYYY-MM');
      this.$emit('update:month', next);
    },
    goToday() {
      const today = this.$moment().format('YYYY-MM');
      this.$emit('update:month', today);
      this.$emit('select-date', this.$moment().format('YYYY-MM-DD'));
    },
    onDayClick(day) {
      if (day.isPast) return;
      this.$emit('select-date', day.date);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_tokens.scss';

.public-month-grid {
  padding: $space-6;
  box-shadow: $shadow-card;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $space-4;
  }

  &__title {
    font-weight: $weight-semibold;
    font-size: 22px;
    letter-spacing: -0.01em;
    color: #483CB9;
    text-transform: capitalize;
    text-align: left;
    flex: 1 1 auto;
  }

  &__today {
    color: #483CB9;
    font-weight: $weight-medium;
  }

  &__nav-btn {
    border-radius: $radius-sm;
    border-color: #e0dff5;
    transition: background-color $transition-fast;
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: left;
    font-size: $text-xs;
    color: #8a8f9c;
    padding-bottom: $space-3;
    margin-bottom: $space-2;

    span {
      padding-left: $space-2;
    }
  }

  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: $space-2;
  }

  &__day {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border: 1px solid #ebe9f4;
    border-radius: $radius-md;
    padding: $space-3;
    min-height: 64px;
    font-size: $text-lg;
    font-weight: $weight-medium;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background-color $transition-fast, border-color $transition-fast;

    &:hover {
      background: #f7f6fb;
    }

    &--muted {
      opacity: 0.4;
    }

    &--has-slots {
      background: #e3f6ec;
      border-color: #e3f6ec;

      &:hover {
        background: #d7f0e2;
      }
    }

    &--selected {
      border: 2px solid #483CB9;
      background: #eeecfb;
      padding: calc(#{$space-3} - 1px);
    }

    &--past {
      color: #c7c9d4;
      cursor: default;
      background: transparent;

      &:hover {
        background: transparent;
      }
    }
  }
}
</style>
